import React, { useEffect, useRef, useState } from 'react'
import {
  Modal,
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { analyzeExtractFilesAsync } from '../../services/rulesService'
import LoadingComponent from '../DeterminateLoader'
import { clearState, setIsLoading } from '../../actions/files'
import UploadFiles from './UploadFiles'
import {
  resetConstructionState,
  setShowUploadModal,
} from '../../actions/construction'
import { isDevelop } from '../../utils/Common'
import BookDetails from './BookDetails'
import AnalyzedCodeView from './AnalyzedCodeView'
import { setProjectName } from '../../actions/rule'
import { useNavigate } from 'react-router-dom'

const ModalBackground = () => {
  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100% - 52px)',
        top: '52px',
        background: '#FFF',
        position: 'absolute',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          left: '35%',
          top: '75%',
          position: 'absolute',
          width: '1250px',
          height: '1048px',
          transform: 'rotate(-48.266deg)',
          borderRadius: '1250px',
          backgroundColor: '#CDF765',
          filter: 'blur(300px)',
        }}
      />

      <Box
        sx={{
          left: '75%',
          top: '-10%',
          position: 'absolute',
          width: '818px',
          height: '1280px',
          transform: 'rotate(-30deg)',
          borderRadius: '1280px',
          backgroundColor: '#E5F0FF',
          filter: 'blur(200px)',
        }}
      />

      <Box
        sx={{
          left: '0%',
          top: '-25%',
          position: 'absolute',
          width: '1196px',
          height: '1048px',
          transform: 'rotate(-30.206deg)',
          borderRadius: '1196px',
          backgroundColor: '#1705E5',
          filter: 'blur(350px)',
        }}
      />

      <Box
        sx={{
          left: '20%',
          top: '-50%',
          position: 'absolute',
          width: '818px',
          height: '1280px',
          transform: 'rotate(-30deg)',
          borderRadius: '1280px',
          backgroundColor: '#E5F0FF',
          filter: 'blur(200px)',
        }}
      />

      <Box
        sx={{
          left: '-10%',
          top: '0%',
          position: 'absolute',
          width: '549px',
          height: '100%',
          borderRadius: '100%',
          backgroundColor: '#6FCCDD66',
          filter: 'blur(200px)',
        }}
      />
    </Box>
  )
}

interface ModalComponentProps {}

const ModalComponent: React.FC<ModalComponentProps> = () => {
  const navigate = useNavigate()
  const isShowUploadModal = true
  const is3DviewActive = useSelector(
    (state: RootState) => state.construction.is3DViewActive,
  )
  const drawings = useSelector((state: RootState) => state.drawings)
  const bookExtractPage = useSelector(
    (state: RootState) => state.rule.bookExtractPage,
  )
  const files = drawings.files
  const fileNames = files.map((file) => file.s3Filename)
  const s3FileKey = fileNames[0]
  const splittedS3FIleKey = s3FileKey?.split('/') || []
  const fileName = splittedS3FIleKey[splittedS3FIleKey.length - 1]
  const building = useSelector(
    (state: RootState) => state.construction.convertData.building,
  )
  const isLoading = drawings.isLoading
  const [filesUpload, setFilesUpload] = useState(true)
  const [convertTo3D] = useState(false)
  const [bookNameUrl, setBookNameUrl] = useState('')

  const dispatch = useDispatch<any>()
  const cleanupFunctionsRef = useRef<(() => void)[]>([])

  const isFileLoaded = () => {
    return (
      files.filter((file) => {
        return (
          (!file.pages.length ||
            (file.pages.length && !file.pages[0].isLoading)) &&
          file.s3Filename
        )
      }).length > 0
    )
  }

  const handleButtonClick = () => {
    dispatch(setIsLoading(true))
    setFilesUpload(false)
    let projectName = ''
    if (bookNameUrl.length > 0) {
      const bookNameSplit = bookNameUrl.split('/')
      projectName = bookNameSplit?.[bookNameSplit?.length - 1]
    } else {
      const fileNameSplit = fileName.split('.')
      projectName = fileNameSplit?.[0]
    }

    dispatch(setProjectName(projectName))
    dispatch(
      analyzeExtractFilesAsync({
        fileName: bookNameUrl.length > 0 ? bookNameUrl : fileName,
      }),
    )
  }

  const handleSearchUrl = (e: any) => {
    setBookNameUrl(e.target.value)
  }

  useEffect(() => {
    if (!isShowUploadModal) {
      dispatch(clearState())
    }
  }, [dispatch, isShowUploadModal])

  useEffect(() => {
    if (!isDevelop() && !is3DviewActive) {
      dispatch(setShowUploadModal(true))
    }
  }, [dispatch, is3DviewActive])

  useEffect(() => {
    if (convertTo3D && !isLoading && building.storeys.length) {
      dispatch(setShowUploadModal(false))
    }
  }, [convertTo3D, dispatch, isLoading, building.storeys])

  useEffect(() => {
    return () => {
      dispatch(clearState())
      dispatch(resetConstructionState())
      dispatch(setIsLoading(false))
    }
  }, [dispatch])

  useEffect(() => {
    return () => {
      cleanupFunctionsRef.current.forEach((cleanup) => cleanup())
    }
  }, [])

  const handleClose = () => {
    navigate('/projects')
  }

  return (
    <Modal
      open={isShowUploadModal}
      onClose={handleClose}
      slotProps={{
        backdrop: {
          sx: (theme) => ({
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(248, 248, 248, 0.25)',
          }),
        },
      }}
    >
      <>
        <ModalBackground />
        <Box
          sx={(theme) => ({
            zIndex: theme.zIndex.drawer + 1,
            position: 'absolute',
            top: '53%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '67%',
            bgcolor: isLoading ? '#EEEEEE' : 'background.paper',
            border: 'none',
            outline: 'none',
            borderRadius: '20px',
            boxShadow: '0px 3px 5px 0px rgba(0, 0, 0, 0.25)',
            padding: '40px',
            height: '90%',
            display: 'flex',
            flexDirection: 'row',
            boxSizing: 'border-box',
          })}
        >
          <IconButton
            aria-label='close'
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='10'
              height='10'
              viewBox='0 0 10 10'
              fill='none'
            >
              <path
                d='M1.05542 0.94458L9.16626 9.05542M9.16626 0.94458L1.05542 9.05542'
                stroke='#56003D'
                strokeLinecap='round'
              />
            </svg>
          </IconButton>
          {!isLoading ? (
            <>
              {filesUpload ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    pl: '53px',
                    pr: '15px',
                  }}
                >
                  <Box
                    sx={{
                      gap: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                      }}
                    >
                      <img src='/images/book.png' style={{ width: '120px' }} />
                      <Typography
                        sx={{
                          color: '#04002E',

                          fontSize: '14px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: 'normal',
                        }}
                      >
                        Extractor
                      </Typography>

                      <Typography
                        sx={{
                          color: '#797979',

                          fontSize: '12px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: 'normal',
                          maxWidth: '350px',
                        }}
                      >
                        Upload or search any text based content to find and
                        select rules to apply to the validation process.
                      </Typography>
                    </Box>
                    <Box sx={{ height: '100%' }}>
                      <UploadFiles cleanupFunctionsRef={cleanupFunctionsRef} />
                    </Box>
                    <Box
                      sx={{
                        height: '100%',
                        gap: 2,
                        backgroundColor: '#F5F5F5',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Typography> Search Via Upcodes URL </Typography>
                      <TextField
                        onChange={handleSearchUrl}
                        size='small'
                        sx={{ backgroundColor: 'white', width: '350px' }}
                      ></TextField>
                    </Box>
                    <Box
                      sx={{ flexShrink: 0, ml: 'auto', alignSelf: 'flex-end' }}
                    >
                      <Button
                        variant='contained'
                        disabled={
                          !(bookNameUrl.length > 0) &&
                          (files.length === 0 || !isFileLoaded())
                        }
                        onClick={handleButtonClick}
                        sx={{
                          background: '#56003D',
                          color: 'white',
                          textTransform: 'capitalize',
                          width: '110px',
                          fontSize: '12px',
                          px: '12px',
                          '&:hover': {
                            backgroundColor: '#56003D',
                          },
                        }}
                      >
                        Analyze
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                // Convert Modal
                <>
                  {bookExtractPage === 'EXTRACT_CODE' ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <BookDetails />
                    </Box>
                  ) : (
                    <AnalyzedCodeView />
                  )}
                </>
              )}
            </>
          ) : (
            // Loading screen
            <LoadingComponent text={'Analyzing'} />
          )}
        </Box>
      </>
    </Modal>
  )
}

export default ModalComponent
